<div class="claims-container">
  <div class="claims-header">
    <h2 class="claims-title">{{ 'claimList.title' | localize }}</h2>
  </div>

  <mat-form-field>
    <mat-label>{{ 'claimList.filter.label' | localize }}</mat-label>
    <input matInput (keyup)="applyFilter($event)" placeholder="{{ 'claimList.filter.placeholder' | localize }}" #input>
  </mat-form-field>

  @if (numberOfClaims > 0) {

    <table mat-table [dataSource]="dataSource" class="claims-table" multiTemplateDataRows>

      <!-- ID Column -->
      <ng-container matColumnDef="id">
        <th mat-header-cell *matHeaderCellDef>{{ 'claimList.columns.claimRef' | localize }}</th>
        <td mat-cell *matCellDef="let claim">
          <div class="claim-id-container">
            <button mat-icon-button
                    (click)="toggle(claim); $event.stopPropagation()"
                    class="expand-button"
                    [class.expanded]="isExpanded(claim)">
              <mat-icon>expand_more</mat-icon>
            </button>
            <span class="claim-id"> {{ claim.insuranceCompanyID }} Ref. <code>{{ claim.insuranceClaimReference }}</code> ({{claim.objectType | localize}})
<!--              @if (claim.objectType === ObjectType.CondominiumUnit) {-->
<!--                / {{ claim.adjusterCompanyID }} Ref. <code>{{ claim.lossAdjusterClaimReference }}</code>-->
<!--              }-->
            </span>
          </div>
        </td>
      </ng-container>

      <!-- DEED Column -->
      <ng-container matColumnDef="deed">
        <th mat-header-cell *matHeaderCellDef>{{ 'claimList.columns.deed' | localize }}</th>
        <td mat-cell *matCellDef="let claim">
        <span class="deed-status" [class]="hasDeed(claim) ? 'deed-yes' : 'deed-no'">
          {{ hasDeed(claim) ? ('common.yes' | localize) : ('common.no' | localize) }}
        </span>
        </td>
      </ng-container>

      <!-- ADJUSTMENT METHOD Column -->
      <ng-container matColumnDef="adjustmentMethod">
        <th mat-header-cell *matHeaderCellDef>{{ 'claimList.columns.adjustmentMethod' | localize }}</th>
        <td mat-cell *matCellDef="let claim">
          <span class="adjustment-method">{{ getAdjustmentMethod(claim) }}</span>
        </td>
      </ng-container>

      <!-- REVIEWED Column -->
      <ng-container matColumnDef="reviewed">
        <th mat-header-cell *matHeaderCellDef>{{ 'claimList.columns.reviewed' | localize }}</th>
        <td mat-cell *matCellDef="let claim">
          @if (claim.status === 'completed' || claim.status === 'rejected') {
            <span class="reviewed-date">
              {{ getReviewedDate(claim) | date:'yyyy-MM-dd' }}
            </span>
          } @else {
            <span class="reviewed-empty">-</span>
          }
        </td>
      </ng-container>

      <!-- STATUS Column -->
      <ng-container matColumnDef="status">
        <th mat-header-cell *matHeaderCellDef>{{ 'claimList.columns.status' | localize }}</th>
        <td mat-cell *matCellDef="let claim">
          <mat-chip class="status-chip" [class]="getStatusClass(claim.status)">
            {{ getStatusDisplayText(claim.status) }}
          </mat-chip>
        </td>
      </ng-container>

      <!-- ACTIONS Column -->
      <ng-container matColumnDef="actions">
        <th mat-header-cell *matHeaderCellDef>{{ 'claimList.columns.actions' | localize }}</th>
        <td mat-cell *matCellDef="let claim">
          <button mat-icon-button
                  class="download-button"
                  (click)="downloadReport($event, claim)"
                  [disabled]="claim.status !== 'completed'">
            <mat-icon>download</mat-icon>
            <span class="download-text">{{ 'claimList.actions.download' | localize }}</span>
          </button>
        </td>
      </ng-container>

      <!-- Expanded Detail Row -->
      <ng-container matColumnDef="expandedDetail">
        <td mat-cell *matCellDef="let claim" [attr.colspan]="displayedColumns.length">
          <div class="claim-detail-container"
               [style.display]="isExpanded(claim) ? 'block' : 'none'">
            <div class="claim-detail-content">
              <div class="detail-grid">
                <div class="detail-section">
                  <div class="detail-item">
                    <span class="label">{{ 'claimList.details.adjusterId' | localize }}</span>
                    <span class="value">{{ claim.adjusterUserFullName || '' }}</span>
                  </div>
                  @if (claim.objectType === ObjectType.CondominiumUnit) {
                    <div class="detail-item">
                      <span class="label">{{ 'claimList.details.lossAdjusterRef' | localize }}</span>
                      <span class="value">{{ claim.lossAdjusterClaimReference }}</span>
                    </div>
                  }
                  <div class="detail-item">
                    <span class="label">{{ 'claimList.details.unitOwnerName' | localize }}</span>
                    <span class="value">{{ claim.unitOwnerName }}</span>
                  </div>
                  <div class="detail-item">
                    <span class="label">{{ 'claimList.details.comment' | localize }}</span>
                    <span class="value">{{ claim.comment }}</span>
                  </div>
                </div>

                <div class="detail-section">
                  @if (claim.objectType === ObjectType.CondominiumUnit) {
                    <div class="detail-item">
                      <span class="label">{{ 'claimList.details.adjusterCompany' | localize }}</span>
                      <span class="value">{{ claim.adjusterCompanyID || 'AdjustorCompanyA' }}</span>
                    </div>
                  }
                  <div class="detail-item">
                    <span class="label">{{ 'claimList.details.address' | localize }}</span>
                    <span class="value">{{ claim.address }}</span>
                  </div>
                  <div class="detail-item">
                    <span class="label">{{ 'claimList.details.unitNumber' | localize }}</span>
                    <span class="value">{{ claim.unitNumber }}</span>
                  </div>
                  <div class="detail-item">
                    <span class="label">{{ 'claimList.details.reductionAmount' | localize }}</span>
                    @if (!(claim.noChanges ?? true)) {
                      <span class="value">{{ claim.savingAmount }}</span>
                    } @else {
                      <span class="value"> - </span>
                    }
                  </div>
                </div>

                <div class="detail-section">
                  <div class="detail-item">
                    <span class="label">{{ 'claimList.details.claimId' | localize }}</span>
                    <span class="value">{{ claim.claimID }}</span>
                  </div>
                  @if (claim.objectType == ObjectType.Residence || claim.objectType == 'entireBuilding') {
                    <div class="detail-item">
                      <span class="label">{{ 'claimMetadata.policyNumber.label' | localize }}</span>
                      <span class="value">{{ claim.policyNumber }}</span>
                    </div>
                  } @else {
                    <div class="detail-item">
                      <span class="label">{{ 'claimList.details.insuranceClaimRef' | localize }}</span>
                      <span class="value">{{ claim.insuranceClaimReference }}</span>
                    </div>
                    <div class="detail-item">
                      <span class="label">{{ 'claimList.details.condominiumName' | localize }}</span>
                      <span class="value">{{ claim.condominiumName }}</span>
                    </div>
                  }
                  <div class="detail-item">
                    <span class="label">{{ 'claimList.details.numberOfImages' | localize }}</span>
                    <span class="value">{{ claim.attachmentFilesCount }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </td>
      </ng-container>

      <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
      <tr mat-row *matRowDef="let claim; columns: displayedColumns;"
          class="claim-row"
          [class.expanded-row]="isExpanded(claim)"
          (click)="toggle(claim)"></tr>
      <tr mat-row *matRowDef="let claim; columns: ['expandedDetail']"
          class="detail-row"></tr>

    </table>
    <mat-paginator #paginator [pageSizeOptions]="[10, 20, 50]"
                   (page)="pageChanged($event)"
                   showFirstLastButtons
                   aria-label="Select page of periodic elements">
    </mat-paginator>

  } @else {
    <div class="no-claims">
      <p>{{ 'claimList.noClaimsFound' | localize }}</p>
    </div>
  }
</div>
